import { useCallback, useEffect, useRef, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import { NetworkHealthEndpoint, NetworkSpeedEndpoint, ReleaseHealthOverview } from "@common/models/Analytics.model";
import { useAppContext } from "@main/context/App/App.context";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import useReleasesSocketConsumer from "@main/pages/Release/consumers/useReleasesSocketConsumer";
import useReleaseVersionsConsumer from "@main/pages/Release/consumers/useReleaseVersionsConsumer";
import { TicketListFilters, TicketListLinkParams } from "@main/pages/Release/helpers/ticketListLink";
import { useAnalyticsDeps } from "@main/pages/Release/Release";
import { EndpointHealthData } from "@main/pages/Release/views/MetricCards/EndpointsHealthCard";
import { EndpointSpeedData } from "@main/pages/Release/views/MetricCards/NetworkSpeedCard";
import { RoutePaths } from "@main/router/config/routePaths";
import { useQuery } from "@tanstack/react-query";
import { generatePath, useNavigate } from "react-router-dom";

export default function useReleasesConsumer(urlVersion?: string | null) {
  const { socket } = useAppContext();
  const { analyticsService } = useAnalyticsDeps();
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const navigate = useNavigate();

  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [rejectedUrlVersion, setRejectedUrlVersion] = useState<string | null>(null);
  const verifyingUrlVersionRef = useRef<string | null>(null);

  const {
    releaseVersions,
    isLoading: releasesLoading,
    isFetchingNextPage,
    hasMore,
    loadNext,
    error: releasesError,
    releaseSearch,
    setReleaseSearch,
    ensureVersionKnown,
  } = useReleaseVersionsConsumer();

  useEffect(() => {
    setSelectedVersion(null);
    setRejectedUrlVersion(null);
    verifyingUrlVersionRef.current = null;
  }, [selectedApp?.id]);

  // Prefer a deep-linked `?version=` param. confirm it exists before trusting it.
  // If it doesn't, mark it rejected and let the default-selection effect below pick the latest release.
  useEffect(() => {
    if (!urlVersion || selectedVersion === urlVersion || verifyingUrlVersionRef.current === urlVersion) return;
    if (!selectedWorkspace?.id || !selectedApp?.id) return;

    verifyingUrlVersionRef.current = urlVersion;
    ensureVersionKnown(urlVersion).then((found) => {
      verifyingUrlVersionRef.current = null;
      if (found) {
        setSelectedVersion(urlVersion);
      } else {
        setRejectedUrlVersion(urlVersion);
      }
    });
  }, [urlVersion, selectedVersion, selectedWorkspace?.id, selectedApp?.id, ensureVersionKnown]);

  // Default-select the latest loaded release — either because there's no URL version at all, or
  // because the one requested was just confirmed not to exist.
  useEffect(() => {
    if (selectedVersion || releasesLoading) return;
    if (urlVersion && urlVersion !== rejectedUrlVersion) return;

    const firstVersion = releaseVersions[0];
    if (firstVersion) setSelectedVersion(firstVersion);
  }, [urlVersion, rejectedUrlVersion, releaseVersions, releasesLoading, selectedVersion]);

  const {
    data: releaseStatusData,
    error: releaseStatusError,
    isLoading: releaseStatusLoading,
    refetch: refetchReleaseStatus,
  } = useQuery(["releaseStatus", { selectedApp, selectedVersion }], ({ signal }) => getReleaseStatus(signal), {
    enabled: Boolean(selectedVersion),
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
  });

  useReleasesSocketConsumer({
    socket,
    refetchItems: refetchReleaseStatus,
  });

  async function getReleaseStatus(signal?: AbortSignal): Promise<ReleaseHealthOverview | []> {
    if (!selectedWorkspace || !selectedApp || !selectedVersion) return [];

    const { data } = await analyticsService.getReleaseStats(
      selectedWorkspace?.id ?? "",
      selectedApp?.id ?? "",
      selectedVersion ?? "",
      signal,
    );

    return data ?? [];
  }

  const generateReleaseSummary = useCallback(async () => {
    if (!selectedWorkspace?.id || !selectedApp?.id || !selectedVersion) return [];

    try {
      const { data } = await analyticsService.generateReleaseSummary(
        selectedWorkspace.id,
        selectedApp.id,
        selectedVersion,
      );

      displayToast({
        title: "Generating summary...",
        content: `This runs in the background and will appear automatically when ready.`,
      });

      return data ?? [];
    } catch (e) {
      if (e?.status === 429) {
        displayToast({
          title: "Already up to date",
          content: `This summary was regenerated in the last 24 hours. Try again later.`,
        });
      } else {
        displayToast({
          title: "Something went wrong",
          content: "Please try again.",
        });
      }

      return [];
    }
  }, [analyticsService, selectedVersion, selectedApp?.id, selectedWorkspace?.id]);

  /// Opens a metric's details page, carrying the currently selected version along in the URL.
  const goToMetric = useCallback(
    (metricKey: string) => {
      const path = generatePath(RoutePaths.RELEASE_METRIC_ROOT, {
        workspaceSlug: selectedWorkspace?.slug ?? "",
        appKey: selectedApp?.key ?? "",
        metricKey,
      });
      navigate(selectedVersion ? `${path}?version=${encodeURIComponent(selectedVersion)}` : path);
    },
    [navigate, selectedWorkspace?.slug, selectedApp?.key, selectedVersion],
  );

  /// Opens a ticket list in a new tab, filtered down to the tickets a metric card counted.
  const openTicketList = useCallback(
    (linkFor: (params: TicketListLinkParams) => string, filters: TicketListFilters) => {
      window
        .open(
          linkFor({
            workspaceSlug: selectedWorkspace?.slug ?? "",
            appKey: selectedApp?.key ?? "",
            version: selectedVersion,
            filters,
          }),
          "_blank",
        )
        ?.focus();
    },
    [selectedWorkspace?.slug, selectedApp?.key, selectedVersion],
  );

  const healthData = releaseStatusData && !Array.isArray(releaseStatusData) ? releaseStatusData : undefined;

  const error =
    (releaseStatusError && (releaseStatusError as Error).constructor.name !== "TypeError") ||
    (releasesError && (releasesError as Error).constructor.name !== "TypeError");

  return {
    healthData,
    releaseVersions,
    selectedVersion,
    setSelectedVersion,
    releaseSearch,
    setReleaseSearch,
    generateReleaseSummary,
    goToMetric,
    openTicketList,
    endpointHealthData: mapNetworkHealth(healthData?.network_health?.items ?? []).slice(0, 5),
    networkSpeedData: mapNetworkSpeed(healthData?.network_speed?.items ?? []).slice(0, 5),
    releasesLoading,
    isFetchingNextPage,
    hasMore,
    loadNext,
    releaseStatusLoading,
    refetchReleaseStatus,
    error,
  };
}

function mapNetworkHealth(items: NetworkHealthEndpoint[]): EndpointHealthData[] {
  return items.map((ep) => ({
    url: ep.endpoint,
    method: ep.method,
    score: ep.score,
    data: ep.status_distribution,
    isError:
      ep.status_distribution
        .filter(
          (s) =>
            s.status_class.startsWith("4") ||
            s.status_class.startsWith("5") ||
            s.status_class.startsWith("err") ||
            s.status_class.startsWith("t/o"),
        )
        .reduce((sum, s) => sum + s.percentage, 0) > 25,
    isWarning: false,
  }));
}

function mapNetworkSpeed(items: NetworkSpeedEndpoint[]): EndpointSpeedData[] {
  return items.map((ep) => ({
    url: ep.endpoint,
    method: ep.method,
    time: ep.average_duration,
    score: ep.score,
    isError: ep.is_error,
  }));
}
